import { PRIVILEGES_PER_FEATURE } from './privileges-per-feature.const';
import { NavigationItem } from '@core/models/NavigationItem.mode';
import { environment } from '@env/environment';
import { ToggleMode } from '@core/enums/toggle-mode.enum';
import { APP_ROUTES } from './app-routes.const';

export const ADMIN_TAB_NAVIGATION: NavigationItem[] = [
  {
    title: 'navigation.users',
    route: '/users',
    icon: 'address-card',
  },
  {
    title: 'navigation.notificationAdministration',
    icon: 'bell',
    subItems: [
      {
        title: 'navigation.templateManagement',
        route: '/push-notifications/templates',
        icon: '',
        requiredPrivileges:
          PRIVILEGES_PER_FEATURE.PushNotifications.TemplateManagement,
      },
    ],
  },
  {
    title: 'navigation.infoBanner',
    route: null,
    icon: 'warning',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.AppMaintenance,
    subItems: [
      {
        title: 'navigation.appInfoBanner',
        route: '/app-info-banner',
        icon: '',
        requiredPrivileges: PRIVILEGES_PER_FEATURE.AppMaintenance,
      },
      {
        title: 'navigation.endOfWorld',
        route: '/end-of-world',
        icon: 'skull',
        requiredPrivileges: PRIVILEGES_PER_FEATURE.EndOfWorldBanner,
      },
    ],
  },

  {
    title: 'navigation.appFeatureFlags',
    route: '/feature-flags',
    icon: 'toggle',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.FeatureFlags,
  },
  {
    title: 'navigation.eventHistory',
    route: '/customer-logs-search/new',
    icon: 'eye',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.FrontendEvents,
  },
  {
    title: 'navigation.auditLog',
    route: '/customer-audit-logs-search/new',
    icon: 'history',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.UserAuditLogs,
  },
  {
    title: 'titles.banners',
    route: '/notifications/banners/templates',
    icon: 'megaphone',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.Banners,
  },
  {
    title: 'titles.labels',
    route: '/labels',
    icon: 'tags',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.Labels,
  },
  {
    title: 'navigation.apiThreats',
    route: '/threats',
    icon: 'shield',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.ApiThreats,
  },
  {
    title: 'navigation.shield',
    route: '/shield',
    icon: 'shield',
    requiredPrivileges: PRIVILEGES_PER_FEATURE.Shield,
  },
];

export const CUSTOMER_TAB_NAVIGATION: NavigationItem[] = [
  {
    title: 'navigation.overview',
    route: '',
    icon: 'grid',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.customerProfile',
    route: 'customer-profile',
    icon: 'profile',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.productInformation',
    route: 'product-information',
    icon: 'box',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    showSeparatorAfter: true,
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.accountManagement',
    route: 'account-management',
    icon: 'settings',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.cardManagement',
    route: 'card-management',
    icon: 'credit-card',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
    enabled: environment.featureFlags?.cardManagement,
  },
  {
    title: 'global.transactions',
    route: 'transactions',
    icon: 'list',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    showSeparatorAfter: true,
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.history',
    route: 'history',
    icon: 'clock',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.interactions',
    route: 'interactions',
    icon: 'comments',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.digitalUser',
    route: 'digital-user',
    icon: 'profile',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    showSeparatorAfter: true,
    isCustomerContextRoute: true,
  },
  {
    title: 'navigation.enquiries',
    route: 'enquiries',
    icon: 'question',
    disabledOnRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    isCustomerContextRoute: true,
  },
];

export const B2B_PORTAL_NAVIGATION: NavigationItem[] = [
  {
    title: 'Home',
    route: '/b2b-home',
    icon: 'home',
    routes: ['/b2b-home', '/b2b-funds-data'],
  },
  {
    title: 'Kundenübersicht',
    route: '/b2b-customers',
    icon: 'profile',
    routes: [
      '/b2b-customers',
      '/b2b-customer-details',
      '/b2b-account-statements',
      '/b2b-depot-slips',
    ],
  },
  {
    title: 'Service',
    route: '/b2b-service',
    icon: 'tools',
    routes: ['/b2b-service'],
    showSeparatorAfter: true,
  },
  {
    title: 'Zum B2B Wertpapierportal',
    externalUrl: environment?.b2bPortalTradingUrl,
    icon: 'external-link',
  },
];

export const NAVIGATION_CONFIG = {
  'admin-console': {
    [ToggleMode.Customer]: CUSTOMER_TAB_NAVIGATION,
    [ToggleMode.Admin]: ADMIN_TAB_NAVIGATION,
  },
  'b2b-portal': B2B_PORTAL_NAVIGATION,
};

export const DIGITAL_HUB_TABS_CONFIG = [
  {
    value: ToggleMode.Customer,
    label: 'global.customer',
    defaultRoute: `/${APP_ROUTES.AFE_CUSTOMERS}`,
    enableTab: environment.enableCustomerTab,
    requiredPrivileges: PRIVILEGES_PER_FEATURE.CustomerTab,
    enableInstituteSwitcher: false,
    backNavigation: {
      enabled: true,
      targetRoute: `/${APP_ROUTES.AFE_CUSTOMERS}`,
      labelKey: 'global.searchCustomer',
      icon: {
        back: 'back',
        current: 'profile',
        alt: 'Navigation',
      },
      excludedRoutes: [`/${APP_ROUTES.AFE_CUSTOMERS}`],
    },
  },
  {
    value: ToggleMode.Admin,
    label: 'global.admin',
    defaultRoute: '/users',
    enableTab: true,
    requiredPrivileges: PRIVILEGES_PER_FEATURE.AdminTab,
    enableInstituteSwitcher: true,
    backNavigation: {
      enabled: false,
    },
  },
];
